import { FC, useState } from 'react'
import cn from 'clsx'
import Accordion from './Accordion'
import SocialIcon from './SocialIcon'
import { IconLogo } from '@components/ui/Logo'
import { ArrowRight, Check } from '@components/icons'
import constants from '@constants'

interface FooterProps {
  disclaimerCopy: string
  copyrightText: string
  socialIconListCollection: any
  primaryLinkGroupsCollection: any
  bottomLinksCollection: any
}

const ns = `site-footer`

const Footer: FC<FooterProps> = ({
  disclaimerCopy,
  copyrightText,
  socialIconListCollection,
  primaryLinkGroupsCollection,
  bottomLinksCollection,
}) => {
  const rootClassnames = cn({
    [`${ns}`]: true,
  })

  const [email, setEmail] = useState('')
  const [inlineError, setInlineError] = useState(false)
  const [error, setError] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.key === 'Enter') {
      validateEmail()
    }
  }

  const postData = async () => {
    const options = {
      method: 'POST',
      headers: {
        revision: '2023-01-01',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            list_id: constants.LIST_ID,
            custom_source: 'footer',
            email,
          },
        },
      }),
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${constants.COMPANY_ID}`,
        options
      )
      if (response.ok) {
        setEmail('')
        setInlineError(false)
        setError(false)
        setSubmitted(true)
      } else {
        setError(true)
      }
    } catch (err: any) {
      console.error(err)
    }
  }

  const handleChange = (e: any) => {
    const formatEmail = e.target.value.trim().toUpperCase()
    setEmail(formatEmail)
  }

  const validateEmail = () => {
    const regex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/
    if (email) {
      if (!regex.test(email)) {
        setInlineError(true)
      } else {
        setInlineError(false)
        postData()
      }
    }
  }

  const resetState = () => {
    setEmail('')
    setInlineError(false)
    setError(false)
    setSubmitted(false)
  }

  return (
    <footer className={rootClassnames}>
      <div className={`${ns}__container`}>
        <div className={`${ns}__top-container`}>
          <div className={`${ns}__email-container`}>
            <div className={`${ns}__logo`}>
              <IconLogo />
            </div>
            <div className={`${ns}__email`}>
              <div className={`${ns}__email-heading`}>
                Be the first to know about new products, exclusive offers, and
                much more.
              </div>
              <div className={`${ns}__email-input`}>
                {submitted && (
                  <div className={`${ns}__email-success`} onClick={resetState}>
                    <div className={`${ns}__email-success-icon`}>
                      <Check />
                    </div>
                    <div className={`${ns}__email-success-text`}>
                      Thanks for Subscribing.
                    </div>
                  </div>
                )}
                {error && (
                  <div className={`${ns}__email-error`} onClick={resetState}>
                    <div className={`${ns}__email-error-text`}>
                      Oops! There was a problem.
                    </div>
                  </div>
                )}
                {!submitted && !error && (
                  <>
                    <input
                      id="footer-email-input"
                      placeholder="Enter your email"
                      onKeyUp={handleKeyUp}
                      value={email}
                      onChange={handleChange}
                    />
                    {inlineError && (
                      <div className={`${ns}__email-inline-error`}>
                        Invalid Email Format
                      </div>
                    )}
                    <div
                      className={`${ns}__email-icon`}
                      onClick={validateEmail}
                    >
                      <ArrowRight />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={`${ns}__divider`}></div>
        <div className={`${ns}__bottom-container`}>
          <div className={`${ns}__left`}>
            <div className={`${ns}__social`}>
              {(socialIconListCollection?.items || []).map((item: any) => {
                return <SocialIcon item={item} key={item.target} />
              })}
            </div>
            <div className={`${ns}__disclaimer-boxed`}>
              * These statements have not been evaluated by the Food and Drug
              Administration. Products are not intended to diagnose, treat, cure
              or prevent any disease.
            </div>
            <div className={`${ns}__disclaimer`}>{disclaimerCopy}</div>
          </div>
          <div className={`${ns}__right`}>
            <div className={`${ns}__accordion`}>
              <Accordion items={primaryLinkGroupsCollection?.items} />
            </div>
          </div>
        </div>
        <div className={`${ns}__bottom-container ${ns}__bottom-links`}>
          <div className={`${ns}__bottom-links-container`}>
            {(bottomLinksCollection?.items || []).map((item: any) => {
              return (
                <a
                  href={item.target}
                  key={item.target}
                  className={`${ns}__bottom-links`}
                >
                  <p>{item.label}</p>
                </a>
              )
            })}
          </div>
          <div className={`${ns}__copyright`}>{copyrightText}</div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
